import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function SignUp() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/signup", {
        name, email, password,
        redirect_base: window.location.origin,
      });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sign up failed");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email, redirect_base: window.location.origin });
      toast.success("Verification email sent");
    } catch { toast.error("Failed to resend"); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <div className="max-w-sm w-full text-center space-y-4" data-testid="signup-done">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <EnvelopeSimple size={28} weight="duotone" className="text-primary" />
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Check your email</h2>
          <p className="text-sm text-muted-foreground">
            We've sent a verification link to <span className="font-medium text-foreground">{email}</span>.
            Click it to activate your Vyapar CRM account.
          </p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" size="sm" onClick={resend} data-testid="resend-button">Resend email</Button>
            <Link to="/login"><Button size="sm" className="rounded-full">Back to sign in</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="signup-form" autoComplete="off">
        <Brand />
        <div>
          <div className="overline text-muted-foreground mb-2">Get started</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Create your account</h2>
          <p className="text-sm text-muted-foreground mt-1">Start managing your business in minutes.</p>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">FULL NAME</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required
              autoComplete="off" placeholder="Your name" className="mt-1.5" data-testid="signup-name-input" />
          </div>
          <div>
            <Label className="text-xs">EMAIL</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              autoComplete="off" placeholder="you@company.com" className="mt-1.5" data-testid="signup-email-input" />
          </div>
          <div>
            <Label className="text-xs">PASSWORD</Label>
            <div className="relative mt-1.5">
              <Input type={show ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} required
                autoComplete="new-password" placeholder="At least 8 characters"
                data-testid="signup-password-input" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">CONFIRM PASSWORD</Label>
            <Input type={show ? "text" : "password"} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required
              autoComplete="new-password" placeholder="Repeat password" className="mt-1.5"
              data-testid="signup-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="signup-submit-button">
          {loading ? "Creating…" : "Create account"}
        </Button>
        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className="bg-background px-2">or</span>
          </div>
        </div>
        <GoogleSignInButton label="Sign up with Google" testId="signup-google-button" />
        <div className="text-sm text-center text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="back-to-login">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
